import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout, Spin, Result, ConfigProvider } from 'antd'
import thTH from 'antd/locale/th_TH'
import dayjs from 'dayjs'
import 'dayjs/locale/th'


import { useAuth } from './contexts/AuthContext'
import LoginPage from './pages/Auth/LoginPage'
import DashboardLayout from './components/Layout/DashboardLayout'
import Dashboard from './pages/Dashboard/DashboardWithQuote'
import ProductsPage from './pages/Products/ProductsPage'
import ContactsPage from './pages/Contacts/ContactsPage'
//import QuotesPage from './pages/Quotes/QuotesPage'
import QuotesPage from './pages/Quotes/QuotePageWithVatUnit'
import ReceiptsPage from './pages/Receipts/ReceiptsPage'
import ExpensesPage from './pages/Expenses/ExpensesPage'
import CalendarPage from './pages/Calendar/CalendarPage'
import NotesPage from './pages/Notes/NotesPage'
import ReportsPage from './pages/Reports/ReportsPage'
import UsersPage from './pages/Users/UsersPage'
import UploadPage from './pages/Upload/UploadPage'
import CompanySettingsPage from './pages/Settings/CompanySettingsPage'
import PurchaseManagement from './pages/Purchase/PurchaseManagement'
import CreatePurchaseOrder from './pages/Purchase/CreatePurchaseOrderPage';
import CreateReceiptPage from './pages/Purchase/CreateReceiptPage';
import SuppliersManagement from './pages/Purchase/SuppliersManagement';
import PurchaseOrderDetails from './pages/Purchase/PurchaseOrderDetails';
import ReceiptsPageWithBarcode from './pages/Receipts/ReceiptsPageWithBarcode'
import POSPage from './pages/Receipts/POSPage'
import DeliveryNotesPage from './pages/DeliveryNotes/DeliveryNotesPage';
import PendingDeliveriesPage from './pages/PendingDeliveries/PendingDeliveriesPage_Complete'
import ApiSettingsPage from './pages/Settings/ApiSettingsPage';
import UnitsManagementPage from './pages/Units/UnitsManagementPage';
import ProductCategoriesManagementPage from './pages/Products/ProductCategoriesManagementPage';

import ProjectsListPage from './pages/Projects/ProjectsListPage'
import CreateProjectPage from './pages/Projects/CreateProjectPage'
import EditProjectPage from './pages/Projects/EditProjectPage'
import ProjectTasksPage from './pages/Projects/ProjectTasksPage'
import TasksPage from './pages/Task/TasksPage'
import ProjectDetail from './pages/Projects/ProjectDetail'
import DirectorAdvancePage from './pages/DirectorAdvance/DirectorAdvancePage'
import DirectorAdvanceReport from './pages/DirectorAdvance/DirectorAdvanceReport'
import BankStatementImportPage from './pages/BankStatement/BankStatementImportPage'
import ChartOfAccountsPage from './pages/Accounting/ChartOfAccountsPage'
import BankAccountsPage from './pages/Accounting/BankAccountsPage'
import JournalEntriesPage from './pages/Accounting/JournalEntriesPage'
import TransfersPage from './pages/Accounting/TransfersPage'
import BankDepositsPage from './pages/Accounting/BankDepositsPage'


dayjs.locale('th')

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="กำลังโหลด..." />
      </Layout>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return children
}

// Public Route Component (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {

  const { user, loading } = useAuth()

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="กำลังโหลด..." />
      </Layout>
    )
  }

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

function App() {
  return (
    <ConfigProvider locale={thTH}>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/products/*" element={<ProductsPage />} />
                    <Route path="/contacts/*" element={<ContactsPage />} />
                    <Route path="/quotes/*" element={<QuotesPage />} />
                    <Route path="/delivery-notes/*" element={<DeliveryNotesPage />} />
                    <Route path="/delivery-pending/*" element={<PendingDeliveriesPage />} />
                    <Route path="/receipts/*" element={<ReceiptsPageWithBarcode />} />
                    {/* <Route path="/receipts/barcode" element={<ReceiptsPageWithBarcode />} /> */}
                    <Route path="/receipts/pos" element={<POSPage />} />
                    <Route path="/expenses/*" element={<ExpensesPage />} />

                    <Route path="/projects" element={<ProjectsListPage />} />
                    <Route path="/projects/create" element={<CreateProjectPage />} />
                    <Route path="/projects/edit/:projectId" element={<EditProjectPage />} />
                    <Route path="/projects/:projectId/tasks" element={<ProjectDetail />} />
                    <Route path="/projects/:projectId/kanban" element={<ProjectTasksPage />} />

                    <Route path="/director-advance" element={<DirectorAdvancePage />} />
                    <Route path="/director-advance/report" element={<DirectorAdvanceReport />} />
                    <Route path="/bank-statement-import" element={<BankStatementImportPage />} />

                    {/* Accounting System */}
                    <Route path="/accounting/chart-of-accounts" element={<ChartOfAccountsPage />} />
                    <Route path="/accounting/bank-accounts" element={<BankAccountsPage />} />
                    <Route path="/accounting/journal-entries" element={<JournalEntriesPage />} />
                    <Route path="/accounting/transfers" element={<TransfersPage />} />
                    <Route path="/accounting/deposits" element={<BankDepositsPage />} />


                    <Route path="/calendar" element={<CalendarPage />} />
                    <Route path="/notes" element={<NotesPage />} />
                    <Route path="/reports/*" element={<ReportsPage />} />
                    <Route path="/users" element={<UsersPage />} />
                    <Route path="/upload" element={<UploadPage />} />
                    <Route path="/settings/company" element={<CompanySettingsPage />} />
                    <Route path="/settings/api" element={<ApiSettingsPage />} />
                    <Route path="/settings/units" element={<UnitsManagementPage />} />
                    <Route path="/settings/product-categories" element={<ProductCategoriesManagementPage />} />
                    {/* หน้าจัดการใบสั่งซื้อหลัก */}
                    <Route path="/purchases" element={<PurchaseManagement />} />

                    {/* หน้าสร้างใบสั่งซื้อใหม่ */}
                    <Route path="/purchases/create" element={<CreatePurchaseOrder />} />

                    {/* หน้าดูรายละเอียดใบสั่งซื้อ */}
                    <Route path="/purchases/view/:id" element={<PurchaseOrderDetails />} />

                    {/* หน้าแก้ไขใบสั่งซื้อ */}
                    <Route path="/purchases/edit/:id" element={<CreatePurchaseOrder />} />

                    {/* หน้าจัดการผู้จำหน่าย */}
                    <Route path="/purchases/suppliers" element={<SuppliersManagement />} />

                    {/* === Goods Receipts Routes === */}

                    {/* หน้าสร้างใบรับสินค้าเข้าคลัง */}
                    <Route path="/purchases/goods-receipt/create" element={<CreateReceiptPage />} />
                    <Route path="/purchases/goods-receipt/create/:id" element={<CreateReceiptPage />} />
                    {/* <Route path="*" element={<Navigate to="/dashboard" replace />} /> */}
                  </Routes>
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </ConfigProvider>
  )
}

export default App